// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek/Marc Planeck

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import Select, {
  OnChangeValue,
  ActionMeta,
  SelectComponentsConfig,
  MultiValue,
  SingleValue,
  components,
  DropdownIndicatorProps,
  NoticeProps,
  SelectInstance,
  GroupBase,
} from "react-select";
import { defineMessages, FormattedMessage, IntlContext, IntlShape } from "react-intl";
import { MessageDescriptor } from "react-intl";

import { IState } from "states/states";
import { IAction } from "actions";
import { isSet } from "shared/utils/utils";
import cssContent from "constants.module.scss";

export type IDropdownSelectOption = {
  index: number;
  id: number;
  source?: "input" | "output";
  // value is needed for the react-Select component -> value is used to
  // evaluate if an entry is selected or not - it needs to be unique inside the list!
  value: string;
  // visible text of the Dropdown-Entry
  label: string;
  // is the entry visible (only relevant in combination with graph-legends)
  visible: boolean;
};

type ISelectOptions = {
  label: string | JSX.Element;
  options: IDropdownSelectOption[];
};

interface IDropdownSelectExtProps {
  isMulti?: boolean;
  components?: SelectComponentsConfig<any, any, any>;
  closeMenuOnSelect?: boolean;
  placeholder?: JSX.Element;
  onChange?: (newValue: OnChangeValue<any, any>, actionMeta: ActionMeta<any>) => void;
  allowSelectAll?: boolean;
  options: ISelectOptions[];
  value?: IDropdownSelectOption[];
  noOptionsString?: JSX.Element;
}

interface IDropdownSelectProps {}

interface IDropdownSelectState {
  inputText: string;
}

const mapStateToProps = (state: IState) => {
  const props: IDropdownSelectProps = {};

  return props;
};

interface IDropdownSelectDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IDropdownSelectDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  selectAll: {
    id: "Select All",
  },
});

export const SELECT_ALL_OPTION_ITEMS = -1;

export const allOption: IDropdownSelectOption = {
  value: "all-1",
  index: SELECT_ALL_OPTION_ITEMS,
  id: SELECT_ALL_OPTION_ITEMS,
  visible: true,
  label: messages.selectAll.id,
};
class DropdownSelectComponent extends React.Component<
  IDropdownSelectProps & IDropdownSelectExtProps & IDropdownSelectDispatch,
  IDropdownSelectState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state = {
    inputText: "",
  };

  // gives styles.menu direct access to the control's DOM node (react-select
  // publicly exposes it as `controlRef`) so it can measure the control's own
  // position for the viewport clamp below, independent of any other style
  // callback
  // typed as `unknown`/GroupBase<unknown> to match what TS already infers for
  // the untyped <Select> element below (ISelectOptions.label being a JSX.Element
  // is incompatible with react-select's GroupBase, same reason the rest of this
  // file falls back to `any` for these generics) - controlRef itself isn't
  // generic-dependent, so this loses no real type safety over that JSX element
  private selectRef = React.createRef<SelectInstance<unknown, boolean, GroupBase<unknown>>>();

  private onChange(
    selected: MultiValue<IDropdownSelectOption> | SingleValue<IDropdownSelectOption> | null,
    event: ActionMeta<IDropdownSelectOption>,
  ) {
    const inSelected = selected as IDropdownSelectOption[];
    if (inSelected !== null && inSelected.length > 0) {
      if (event.option === allOption) {
        if (inSelected.includes(allOption)) {
          const selectedOptions: IDropdownSelectOption[] = [];
          this.props.options.forEach((optionEntry) => {
            selectedOptions.push(...optionEntry.options);
          });
          return this.props.onChange?.([event.option, ...selectedOptions], event);
        } else {
          return this.props.onChange?.([], event);
        }
      } else {
        let availableItems = 0;
        this.props.options.forEach((group) => {
          availableItems += group.options.length;
        });

        if (
          inSelected.some((item) => {
            return item.index === allOption.index;
          })
        ) {
          if (inSelected.length === 1) {
            return this.props.onChange?.([], event);
          } else if (inSelected.length === availableItems) {
            return this.props.onChange?.(
              inSelected.filter((option: IDropdownSelectOption) => {
                return option.value !== allOption.value;
              }),
              event,
            );
          }
        } else {
          if (inSelected.length === availableItems) {
            const selectedOptions: IDropdownSelectOption[] = [];
            this.props.options.forEach((optionEntry) => {
              selectedOptions.push(...optionEntry.options);
            });
            return this.props.onChange?.([allOption, ...selectedOptions], event);
          }
        }
      }
    }

    return this.props.onChange?.(selected, event);
  }

  public render() {
    const DropdownIndicator = (props: DropdownIndicatorProps<any, any>) => {
      return <div className="dropdown-toggle me-2"></div>;
    };

    const NoOptionsMessage = (props: NoticeProps) => {
      return (
        <components.NoOptionsMessage {...props}>
          <span className="custom-css-class">
            {this.props.noOptionsString ?? <FormattedMessage id="No option available" />}
          </span>
        </components.NoOptionsMessage>
      );
    };

    return (
      <Select
        ref={this.selectRef}
        isMulti={this.props.isMulti}
        closeMenuOnSelect={this.props.closeMenuOnSelect}
        placeholder={this.props.placeholder}
        value={this.props.value}
        className="btn-header dropdownselect"
        // render the menu in a portal on <body>, positioned via fixed coordinates
        // computed from the control's real screen position - this escapes any
        // ancestor's overflow/stacking/positioning quirks that otherwise caused the
        // menu to be clipped or misplaced off the edges of small screens.
        // classNamePrefix exposes stable classes (e.g. .dropdownselect__menu-portal)
        // so the small-screen width clamp in content.scss can target the portal,
        // which lives outside this component's DOM subtree
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        classNamePrefix="dropdownselect"
        styles={{
          option: (base, { isFocused }) => ({
            ...base,
            backgroundColor:
              isFocused ? cssContent.DROPDOWN_BACKGROUND : cssContent.DROPDOWN_TRANSPARENT,
            // long labels must stay on one line so they overflow horizontally
            // into the menu's own scrollbar instead of wrapping/growing the menu
            whiteSpace: "nowrap",
            ":hover": {
              backgroundColor: cssContent.DROPDOWN_BACKGROUND,
            },
          }),
          container: (provided) => ({
            ...provided,
            width: "auto !important",
            // flex items default to min-width: auto, which lets their content's natural
            // width (e.g. a long placeholder) push this out of the screen instead of
            // letting the value/placeholder's own ellipsis truncation take over
            minWidth: 0,
            maxWidth: "100%",
            zIndex: Number(cssContent.DROPDOWN_BUTTON_Z_INDEX) + 1,
            height: "35px",
            minHeight: "35px",
            ":hover": {
              backgroundColor: cssContent.DROPDOWN_BACKGROUND,
            },
          }),
          menu: (defaultStyles) => {
            // cap how far the menu can grow to the right of the control so it
            // never runs past the viewport edge, while still letting it shrink
            // to fit short labels instead of always spanning that full budget.
            // Computed against the viewport (not the portal's own box) because
            // the portal's default width is pinned to the control's width by
            // react-select, so a percentage-based clamp here would collapse
            // the menu back down to the button's width instead of its content.
            // Measured directly off the control's DOM node (exposed by
            // react-select as controlRef) rather than via another style
            // callback, so this is self-contained.
            const controlLeft = this.selectRef.current?.controlRef?.getBoundingClientRect().left;
            return {
              ...defaultStyles,
              marginTop: 0,
              width: "max-content",
              minWidth:
                isSet(controlLeft) ?
                  `min(100%, calc(100vw - ${controlLeft}px - 16px))`
                : "min(100%, calc(100vw - 16px))",
              maxWidth:
                isSet(controlLeft) ? `calc(100vw - ${controlLeft}px - 16px)` : "calc(100vw - 16px)",
              overflowX: "auto",
              zIndex: cssContent.DROPDOWN_FOREGROUND_Z_INDEX,
            };
          },
          valueContainer: (css) => ({
            ...css,
            width: "auto",
            height: "35px",
            overflow: "hidden",
            flexWrap: "nowrap",
          }),
          placeholder: (defaultStyles) => ({
            ...defaultStyles,
            backgroundColor: cssContent.DROPDOWN_TRANSPARENT,
            color: cssContent.DROPDOWN_TEXTCOLOR,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }),
          control: (defaultStyles) => ({
            ...defaultStyles,
            backgroundColor: cssContent.DROPDOWN_TRANSPARENT,
            minHeight: "35px",
            height: "35px",
            borderRadius: "6px",
            border: "none",
            // This line disable the blue border on focus
            boxShadow: "none",
          }),
          indicatorSeparator: (defaultStyles) => ({
            ...defaultStyles,
            backgroundColor: cssContent.DROPDOWN_BACKGROUND,
            border: "none",
          }),
          dropdownIndicator: (defaultStyles) => ({
            ...defaultStyles,
            color: cssContent.DROPDOWN_TEXTCOLOR,
          }),
          menuPortal: (provided) => ({
            ...provided,
            minWidth: "auto",
            zIndex: cssContent.DROPDOWN_FOREGROUND_Z_INDEX,
          }),
          menuList: (provided) => ({
            ...provided,
            minWidth: "auto",
            height: "250px",
            overflowX: "auto",
          }),
        }}
        components={{
          ...components,
          ...this.props.components,
          DropdownIndicator,
          NoOptionsMessage,
        }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isClearable={false}
        blurInputOnSelect={false}
        options={this.translateOptions(
          this.props.allowSelectAll ? [allOption, ...this.props.options] : this.props.options,
        )}
        onChange={(selected, event) =>
          this.onChange(
            selected as MultiValue<IDropdownSelectOption>,
            event as ActionMeta<IDropdownSelectOption>,
          )
        }
        tabSelectsValue={true}
        onInputChange={(newValue, actionMeta) => {
          if (actionMeta.action === "input-change" || actionMeta.action === "menu-close") {
            this.setState({ inputText: newValue });
            return newValue;
          }
        }}
        inputValue={this.state.inputText}
      />
    );
  }

  private translateOptions(options: (IDropdownSelectOption | ISelectOptions)[]) {
    return options.map((curItem) => {
      // translate label if entry is of type MessageDescriptor
      if (isSet((curItem.label as MessageDescriptor)?.id)) {
        curItem.label = this.context.formatMessage(curItem.label as MessageDescriptor);
      }
      return curItem;
    });
  }
}

const DropdownSelect = connect(mapStateToProps, mapDispatchToProps)(DropdownSelectComponent);

export default DropdownSelect;
